import React, { useState, useEffect } from 'react';
import { 
  Crosshair, 
  DollarSign, 
  Activity, 
  Target,
  Boxes,
  ShoppingCart
} from 'lucide-react';
import { analyticsAPI, firearmsAPI, purchasesAPI, loadsAPI, firingSessionsAPI } from '../services/api';
import { formatLocalDate } from '../services/date';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalFirearms: 0,
    totalPurchases: 0,
    totalSpent: 0,
    totalLoads: 0,
    totalRounds: 0,
    totalSessions: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [costByCaliber, setCostByCaliber] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load basic stats
      const [firearmsRes, purchasesRes, loadsRes, sessionsRes, costRes] = await Promise.all([
        firearmsAPI.getAll(),
        purchasesAPI.getAll(),
        loadsAPI.getAll(),
        firingSessionsAPI.getAll(),
        analyticsAPI.getCostByCaliber()
      ]);

      const firearms = firearmsRes.data;
      const purchases = purchasesRes.data;
      const loads = loadsRes.data;
      const sessions = sessionsRes.data;
      const costs = costRes.data;

      const totalSpent = purchases.reduce((sum, p) => sum + p.total_cost, 0);
      const totalRounds = loads.reduce((sum, l) => sum + l.quantity_created, 0);

      setStats({
        totalFirearms: firearms.length,
        totalPurchases: purchases.length,
        totalSpent,
        totalLoads: loads.length,
        totalRounds,
        totalSessions: sessions.length
      });

      setCostByCaliber(costs);

      // Create recent activity feed
      // Use individual load additions from the 'additions' array (not grouped totals)
      const loadAdditions = loads.flatMap(l => 
        (l.additions || []).map(a => ({
          type: 'load',
          date: a.date,
          description: `Created ${a.quantity} rounds of ${l.recipe.name}`,
          amount: a.quantity * (a.cost_per_round || 0)
        }))
      );

      const activity = [
        ...purchases.slice(0, 10).map(p => ({
          type: 'purchase',
          date: p.purchase_date,
          description: `Purchased ${p.quantity} ${p.unit} ${p.component.brand} ${p.component.model} (${p.component.caliber})`,
          amount: p.total_cost
        })),
        ...loadAdditions.slice(0, 10),
        ...sessions.slice(0, 10).map(s => ({
          type: 'session',
          date: s.session_date,
          description: `Fired ${s.rounds_fired} rounds with ${s.firearm ? `${s.firearm.manufacturer} ${s.firearm.model}` : 'Unknown firearm'}`,
          location: s.location
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

      setRecentActivity(activity);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'primary', format = 'number' }) => {
    const formatValue = (val) => {
      if (format === 'currency') {
        return `$${val.toFixed(2)}`;
      }
      return val.toLocaleString();
    };

    return (
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>
              {formatValue(value)}
            </p>
          </div>
          <div className={`p-3 bg-${color}-100 rounded-full`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </div>
    );
  };

  const ActivityIcon = ({ type }) => {
    switch (type) {
      case 'purchase':
        return <ShoppingCart className="h-5 w-5 text-green-600" />;
      case 'load':
        return <Boxes className="h-5 w-5 text-blue-600" />;
      case 'session':
        return <Target className="h-5 w-5 text-orange-600" />;
      default:
        return <Activity className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Overview of your reloading activities</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <StatCard
          title="Firearms"
          value={stats.totalFirearms}
          icon={Crosshair}
          color="blue"
        />
        <StatCard
          title="Purchases"
          value={stats.totalPurchases}
          icon={ShoppingCart}
          color="green"
        />
        <StatCard
          title="Total Spent"
          value={stats.totalSpent}
          icon={DollarSign}
          color="red"
          format="currency"
        />
        <StatCard
          title="Load Batches"
          value={stats.totalLoads}
          icon={Boxes}
          color="purple"
        />
        <StatCard
          title="Rounds Created"
          value={stats.totalRounds}
          icon={Target}
          color="orange"
        />
        <StatCard
          title="Firing Sessions"
          value={stats.totalSessions}
          icon={Activity}
          color="indigo"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Caliber */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost by Caliber</h3>
          <div className="space-y-4">
            {costByCaliber.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{item.caliber}</p>
                  <p className="text-sm text-gray-600">{item.total_rounds} rounds</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">${item.total_cost.toFixed(2)}</p>
            <p className="text-sm text-gray-600">${item.cost_per_round.toFixed(2)}/round</p>
                </div>
              </div>
            ))}
            {costByCaliber.length === 0 && (
              <p className="text-gray-500 text-center py-4">No cost data available</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <ActivityIcon type={activity.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-xs text-gray-500">
                      {formatLocalDate(activity.date)}
                    </p>
                    {activity.amount && (
                      <span className="text-xs text-green-600 font-medium">
                        ${activity.amount.toFixed(2)}
                      </span>
                    )}
                    {activity.location && (
                      <span className="text-xs text-gray-500">
                        @ {activity.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {recentActivity.length === 0 && (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;